/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { Space } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { PlatformList } from '@/components/PlatformList'
import { TPlatformListService } from '@/components/PlatformList/interface'
import { useEnumFilter } from '@/hooks/useEnumFilter'
import { useTimeRangeFilter } from '@/hooks/useTimeRangeFilter'
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { VoucherRedeemedRecords } from '@/pages/billing/Vouchers/VoucherRedeemedRecords'
import { useVouchersColumns } from '@/pages/billing/Vouchers/useVouchersColumns'
import { VoucherState } from '@/pages/billing/interface'
import { accountClient } from '@/services/cloudClient/accountClient'
import { createOrders } from '@/utils/createOrders'

export function Vouchers() {
  const { t } = useTranslation()
  const { contractId } = useParams()
  const { filter: stateSelect, value: stateSelected } = useEnumFilter<VoucherState>(VoucherState, t('All states'))
  const { filter: timeRangeFilter, timeRange } = useTimeRangeFilter(null, { format: 'YYYY-MM', picker: 'month', allowClear: true })
  const columns = useVouchersColumns()
  const emitter = PlatformList.useCreateEmitter()

  const service: TPlatformListService = ({ pagination, sorter }, options) => {
    const { current, pageSize } = pagination || {}
    return accountClient.voucherApi.listVouchers(
      contractId,
      {
        pageNum: current,
        pageSize,
        voucherStates: stateSelected ? [stateSelected as VoucherState] : undefined,
        start: timeRange?.[0]?.valueOf(),
        end: timeRange?.[1]?.valueOf(),
        tag: undefined,
        type: undefined,
        ...createOrders(sorter),
      },
      options,
    )
  }

  useEffect(() => {
    emitter.fire({
      pagination: {
        current: 1,
        pageSize: 10,
      },
    })
  }, [stateSelected, timeRange])

  return (
    <LayoutOthers.MainContent title={t('Vouchers')}>
      <LayoutOthers.Alert className="margin-bottom-20">
        <div>· {t('vouchers_desc_1')}</div>
        <div>· {t('vouchers_desc_2')}</div>
        <div>· {t('vouchers_desc_3')}</div>
      </LayoutOthers.Alert>
      <LayoutOthers.ContentCard title={t('Distributed Records')} isTable>
        <PlatformList
          emitter={emitter}
          service={service}
          columns={columns}
          createTitle={() => (
            <>
              <Space>
                {stateSelect}
                {timeRangeFilter}
              </Space>
            </>
          )}
        />
      </LayoutOthers.ContentCard>
      <LayoutOthers.ContentCard title={t('Redeemed Records')} isTable>
        <VoucherRedeemedRecords />
      </LayoutOthers.ContentCard>
    </LayoutOthers.MainContent>
  )
}
